/**
 * approval.js — what approval means on the client (Add System Phase 7).
 *
 * Vault: "Add System — Finalization & Consistency (Spec)", "Every place approval appears". The server
 * owns the rules (migration 20261109090000): a non-admin's new item of a type the space marks
 * "Requires approval" is born pending, and a pending or rejected item is visible only to its creator
 * and the space's admins. So anyone who can see a waiting item is one of those two, and the client
 * only has to choose the words and the buttons.
 *
 * `node.approval` is `{ status, by, at, note }` or null (itemFieldMapping → transformItem).
 *
 * Pure, so `node --test` pins the words.
 */

export const WAITING = 'Waiting for approval';
export const SENT_FOR_APPROVAL = 'Sent for approval. Only you and the admins can see it for now.';
export const SUBMIT_FOR_APPROVAL = 'Submit for approval';

/** The item's approval status — 'pending' | 'approved' | 'rejected' — or null when never gated. */
export function approvalStatus(node) {
  const s = node?.approval?.status;
  return s === 'pending' || s === 'approved' || s === 'rejected' ? s : null;
}

/** Waiting, for the faded card and the chip: pending, or rejected (it is out of the space either way). */
export function isHeld(node) {
  const s = approvalStatus(node);
  return s === 'pending' || s === 'rejected';
}

/** The class a card, row or event adds when the item is held. One rule styles it everywhere. */
export function heldClass(node) {
  return isHeld(node) ? 'is-held-for-approval' : '';
}

/** The chip's words. */
export function chipLabel(node) {
  const s = approvalStatus(node);
  if (s === 'pending') return WAITING;
  if (s === 'rejected') return 'Not approved';
  return null;
}

/**
 * The Info tab banner: the sentence, and whether this viewer gets the Approve and Reject buttons.
 * Admins decide; the creator is told where it stands. Approved items get a quiet record line.
 *
 * @param {object} node
 * @param {{ isAdmin?: boolean, meId?: string|null, creatorName?: string|null, deciderName?: string|null,
 *           typeName?: string|null, spaceName?: string|null, when?: (iso: string) => string }} ctx
 * @returns {null | { tone: 'waiting'|'rejected'|'approved', text: string, note: string|null, canDecide: boolean, decision: 'approve'|'reject'|'both' }}
 */
export function approvalBanner(node, { isAdmin = false, meId = null, creatorName = null, deciderName = null, typeName = null, spaceName = null, when = null } = {}) {
  const s = approvalStatus(node);
  if (!s) return null;
  const a = node.approval;
  const mine = !!(meId && node.createdById && node.createdById === meId);
  const someone = mine ? 'You' : (creatorName || 'Someone');
  const what = typeName ? `this ${typeName.toLowerCase()}` : 'this';
  const space = spaceName || 'the space';
  const date = a.at && when ? `, ${when(a.at)}` : '';
  const by = a.by && meId && a.by === meId ? 'you' : (deciderName || 'an admin');

  // `headline` is the status line's words (ApprovalStatus draws its icon); `text` says who can see it.
  if (s === 'pending') {
    return {
      tone: 'waiting',
      headline: WAITING,
      text: isAdmin && !mine
        ? `${someone} added ${what}. It waits for an admin before the rest of ${space} can see it.`
        : `Only you and the admins of ${space} can see it until an admin approves it.`,
      note: null,
      canDecide: isAdmin,
      decision: 'both',
    };
  }
  if (s === 'rejected') {
    return {
      tone: 'rejected',
      headline: `Not approved by ${by}${date}`,
      text: `Only ${mine ? 'you' : 'its creator'} and the admins of ${space} can see it.`,
      note: a.note || null,
      canDecide: isAdmin,
      decision: 'approve',
    };
  }
  return { tone: 'approved', headline: `Approved by ${by}${date}`, text: null, note: null, canDecide: false, decision: 'reject' };
}

/**
 * Does a new item of this type wait for this person? The type record as the app holds it (its space
 * settings laid over by overlayTypeAccess): only a setting saved on the space applies, as on the
 * server, and admins never wait.
 */
export function typeWaits(type, { isAdmin = false } = {}) {
  if (!type || isAdmin) return false;
  // overlayTypeAccess already sets requiresApproval to what the space saved (false when it saved
  // nothing), so it is the whole answer. Requiring accessFromSpace as well was redundant, and wrong
  // whenever a type reached the page by a path that dropped that flag: the button said "Add event"
  // for a type that waits.
  return !!type.requiresApproval;
}

/** The add sheet's save button, when the way being used makes a type that waits. */
export function saveLabel(baseLabel, { waits = false } = {}) {
  return waits ? SUBMIT_FOR_APPROVAL : baseLabel;
}

/** Items in a set that wait — the "N waiting for approval" pill and the Review tab's new section. */
export function waitingIn(nodes) {
  return (nodes || []).filter((n) => approvalStatus(n) === 'pending');
}
